import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CreditCard, Tag, CheckCircle, AlertCircle, Loader2, ArrowLeft } from 'lucide-react';
import Badge from '@/components/Badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/context/AuthContext';
import { getCourseInfoBySlug, getCourseInfo, createCheckout, validateDiscount, getPaymentStatus } from '@/services/api';
import { toast } from 'sonner';

const COURSE_DASHBOARDS = {
  'bail-enforcement': '/dashboard',
  'pre-licensing': '/pre-licensing/dashboard',
  'bail-bond-prelicensing': '/pre-licensing/dashboard',
};

const EnrollPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, paidCourses, refreshUser } = useAuth();
  const [courseData, setCourseData] = useState(null);
  const [discountCode, setDiscountCode] = useState('');
  const [discountApplied, setDiscountApplied] = useState(null);
  const [loading, setLoading] = useState(false);
  const [validatingDiscount, setValidatingDiscount] = useState(false);
  const [checkingPayment, setCheckingPayment] = useState(false);

  const courseSlug = searchParams.get('course');
  const checkoutCourse = courseSlug === 'bail-bond-prelicensing' ? 'pre-licensing' : courseSlug;

  useEffect(() => {
    const fetchCourse = async () => {
      try {
        const res = await getCourseInfoBySlug(courseSlug);
        setCourseData(res.data);
      } catch {
        try {
          const res = await getCourseInfo();
          setCourseData(res.data);
        } catch (e) {
          console.error('Failed to fetch course info:', e);
        }
      }
    };
    fetchCourse();
  }, [courseSlug]);

  // Check payment status if returning from Stripe
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      checkPaymentStatus(sessionId);
    }
  }, [searchParams]);

  // Redirect if already paid for this course
  useEffect(() => {
    if (paidCourses.includes(checkoutCourse) || paidCourses.includes(courseSlug)) {
      const dashboard = COURSE_DASHBOARDS[courseSlug] || COURSE_DASHBOARDS[checkoutCourse] || '/dashboard';
      navigate(dashboard);
    }
  }, [paidCourses, courseSlug, checkoutCourse, navigate]);

  const checkPaymentStatus = async (sessionId) => {
    setCheckingPayment(true);
    let attempts = 0;
    const maxAttempts = 5;

    const poll = async () => {
      try {
        const response = await getPaymentStatus(sessionId);
        if (response.data.payment_status === 'paid') {
          await refreshUser();
          toast.success('Payment successful! Welcome to the course.');
          const dashboard = COURSE_DASHBOARDS[courseSlug] || COURSE_DASHBOARDS[checkoutCourse] || '/dashboard';
          navigate(dashboard);
          return;
        } else if (response.data.status === 'expired') {
          toast.error('Payment session expired. Please try again.');
          setCheckingPayment(false);
          return;
        }
        
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, 2000);
        } else {
          toast.info('Payment is being processed. Please check back shortly.');
          setCheckingPayment(false);
        }
      } catch (error) {
        console.error('Error checking payment:', error);
        setCheckingPayment(false);
      }
    };

    poll();
  };

  const handleValidateDiscount = async () => {
    if (!discountCode.trim()) return;
    
    setValidatingDiscount(true);
    try {
      const response = await validateDiscount(discountCode, checkoutCourse);
      setDiscountApplied(response.data);
      toast.success('Discount code applied!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid discount code');
      setDiscountApplied(null);
    } finally {
      setValidatingDiscount(false);
    }
  };

  const handleEnroll = async () => {
    setLoading(true);
    try {
      const response = await createCheckout(
        window.location.origin,
        discountApplied ? discountCode : null,
        checkoutCourse
      );
      
      if (response.data.free) {
        await refreshUser();
        toast.success('Course access granted!');
        const dashboard = COURSE_DASHBOARDS[courseSlug] || COURSE_DASHBOARDS[checkoutCourse] || '/dashboard';
        navigate(dashboard);
      } else {
        window.location.href = response.data.url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start checkout');
    } finally {
      setLoading(false);
    }
  };

  if (checkingPayment) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-primary animate-spin mx-auto mb-4" />
          <h2 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Barlow Condensed' }}>
            PROCESSING PAYMENT
          </h2>
          <p className="text-muted-foreground">Please wait while we confirm your payment...</p>
        </div>
      </div>
    );
  }

  const coursePrice = courseData?.price || (checkoutCourse === 'pre-licensing' ? 395 : 495);
  const finalPrice = discountApplied ? discountApplied.final_price : coursePrice;
  const courseName = courseData?.title || courseData?.short_title || 'Course';
  const totalChapters = courseData?.total_chapters || courseData?.chapters?.length || '-';
  const totalHours = courseData?.total_hours || '-';
  const features = courseData?.features || [];

  const isEnforcement = checkoutCourse === 'bail-enforcement';

  // If no course selected, show course picker
  if (!courseSlug) {
    const enfPaid = paidCourses.includes('bail-enforcement');
    const plPaid = paidCourses.includes('pre-licensing');
    
    return (
      <div className="min-h-screen bg-background">
        <nav className="bg-background border-b border-border">
          <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/')}>
              <Badge className="w-8 h-8" />
              <span className="font-bold text-xl tracking-tight" style={{ fontFamily: 'Barlow Condensed' }}>
                COLORADO BEA
              </span>
            </div>
            <span className="text-muted-foreground">Welcome, {user?.first_name}</span>
          </div>
        </nav>
        <div className="max-w-4xl mx-auto px-6 py-12">
          <div className="text-center mb-12">
            <h1 className="text-4xl font-black mb-4" style={{ fontFamily: 'Barlow Condensed' }}>
              SELECT A <span className="text-primary">COURSE</span>
            </h1>
            <p className="text-muted-foreground">Choose the course you'd like to enroll in</p>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-card border border-border rounded-sm p-6 hover:border-blue-500 transition-colors cursor-pointer"
              onClick={() => enfPaid ? navigate('/dashboard') : navigate('/enroll?course=bail-enforcement')}
              data-testid="course-pick-enforcement"
            >
              <h2 className="text-xl font-bold mb-2 text-blue-500" style={{ fontFamily: 'Barlow Condensed' }}>
                BAIL ENFORCEMENT CERTIFICATION
              </h2>
              <p className="text-muted-foreground text-sm mb-4">10 Chapters &bull; 24 Hours &bull; $495</p>
              <Button className={enfPaid ? "bg-green-600 hover:bg-green-700" : "bg-blue-700 hover:bg-blue-800"} data-testid="course-pick-enforcement-btn">
                {enfPaid ? 'Go to Dashboard' : 'Enroll — $495'}
              </Button>
            </div>
            <div className="bg-card border border-border rounded-sm p-6 hover:border-amber-500 transition-colors cursor-pointer"
              onClick={() => plPaid ? navigate('/pre-licensing/dashboard') : navigate('/enroll?course=pre-licensing')}
              data-testid="course-pick-prelicensing"
            >
              <h2 className="text-xl font-bold mb-2 text-amber-500" style={{ fontFamily: 'Barlow Condensed' }}>
                BAIL BOND PRE-LICENSING
              </h2>
              <p className="text-muted-foreground text-sm mb-4">7 Chapters &bull; 20 Hours &bull; $395</p>
              <Button className={plPaid ? "bg-green-600 hover:bg-green-700" : "bg-amber-600 hover:bg-amber-700"} data-testid="course-pick-prelicensing-btn">
                {plPaid ? 'Go to Dashboard' : 'Enroll — $395'}
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <nav className="bg-background border-b border-border">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/')}>
            <Badge className="w-8 h-8" />
            <span className="font-bold text-xl tracking-tight" style={{ fontFamily: 'Barlow Condensed' }}>
              COLORADO BEA
            </span>
          </div>
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate('/')} data-testid="enroll-back-btn">
              <ArrowLeft className="w-4 h-4 mr-1" /> Back to Courses
            </Button>
            <span className="text-muted-foreground">
              Welcome, {user?.first_name}
            </span>
          </div>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-black mb-4" style={{ fontFamily: 'Barlow Condensed' }}>
            COMPLETE YOUR <span className={isEnforcement ? "text-blue-500" : "text-amber-500"}>ENROLLMENT</span>
          </h1>
          <p className="text-muted-foreground">
            You're one step away from starting {courseName}
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {/* Course Summary */}
          <div className="bg-card border border-border rounded-sm p-6">
            <h2 className="text-xl font-bold mb-4" style={{ fontFamily: 'Barlow Condensed' }}>
              COURSE SUMMARY
            </h2>
            
            <div className="space-y-4">
              <div className="flex justify-between py-2 border-b border-border">
                <span className="text-muted-foreground">Course</span>
                <span className="text-right text-sm font-medium">{courseName}</span>
              </div>
              <div className="flex justify-between py-2 border-b border-border">
                <span className="text-muted-foreground">Credit Hours</span>
                <span>{totalHours} Hours</span>
              </div>
              <div className="flex justify-between py-2 border-b border-border">
                <span className="text-muted-foreground">Chapters</span>
                <span>{totalChapters} Chapters</span>
              </div>
              <div className="flex justify-between py-2 border-b border-border">
                <span className="text-muted-foreground">Final Exam</span>
                <span>150 Questions</span>
              </div>
              <div className="flex justify-between py-2 border-b border-border">
                <span className="text-muted-foreground">Certificate</span>
                <span className={isEnforcement ? "text-blue-500" : "text-amber-500"}>Included</span>
              </div>
            </div>

            {features.length > 0 && (
              <div className="mt-6 p-4 bg-secondary/50 rounded-sm">
                <h3 className="font-semibold mb-2">What's Included:</h3>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  {features.slice(0, 6).map((f, i) => (
                    <li key={i} className="flex items-center gap-2">
                      <CheckCircle className={`w-4 h-4 ${isEnforcement ? 'text-blue-500' : 'text-amber-500'}`} />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Payment Section */}
          <div className="bg-card border border-border rounded-sm p-6">
            <h2 className="text-xl font-bold mb-4" style={{ fontFamily: 'Barlow Condensed' }}>
              PAYMENT
            </h2>

            {/* Discount Code */}
            <div className="mb-6">
              <Label htmlFor="discount">Discount Code</Label>
              <div className="flex gap-2 mt-1">
                <div className="relative flex-1">
                  <Tag className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="discount"
                    placeholder="Enter code"
                    className="pl-10 uppercase"
                    value={discountCode}
                    onChange={(e) => setDiscountCode(e.target.value.toUpperCase())}
                    data-testid="enroll-discount-input"
                  />
                </div>
                <Button 
                  variant="outline" 
                  onClick={handleValidateDiscount}
                  disabled={validatingDiscount || !discountCode.trim()}
                  data-testid="enroll-apply-discount-btn"
                >
                  {validatingDiscount ? 'Checking...' : 'Apply'}
                </Button>
              </div>
              {discountApplied && (
                <div className="mt-2 p-2 bg-primary/10 border border-primary/30 rounded-sm flex items-center gap-2 text-sm">
                  <CheckCircle className="w-4 h-4 text-primary" />
                  <span>{discountApplied.discount_percent}% discount applied!</span>
                </div>
              )}
            </div>

            {/* Price Summary */}
            <div className="space-y-3 mb-6">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Course Price</span>
                <span>${coursePrice.toFixed(2)}</span>
              </div>
              {discountApplied && (
                <div className="flex justify-between text-primary">
                  <span>Discount ({discountApplied.discount_percent}%)</span>
                  <span>-${discountApplied.discount_amount.toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between text-xl font-bold pt-3 border-t border-border">
                <span>Total</span>
                <span className={isEnforcement ? "text-blue-500" : "text-amber-500"}>${finalPrice.toFixed(2)}</span>
              </div>
            </div>

            <Button 
              className={`w-full font-bold ${isEnforcement ? 'bg-blue-700 hover:bg-blue-800' : 'bg-amber-600 hover:bg-amber-700'} text-white`}
              onClick={handleEnroll}
              disabled={loading}
              data-testid="enroll-submit-btn"
            >
              <CreditCard className="w-5 h-5 mr-2" />
              {loading ? 'PROCESSING...' : finalPrice === 0 ? 'ENROLL FREE' : `PAY $${finalPrice.toFixed(2)}`}
            </Button>

            <p className="text-xs text-muted-foreground text-center mt-4">
              Secure payment processed by Stripe
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EnrollPage;
